import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Exploring One-Variable Data",
  examWeight: "15–23%",
  focus: "Variation & Distribution, Patterns & Uncertainty",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Introducing Statistics: What Can We Learn from Data?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Cycle of Statistics</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Statistics is the science of learning from data. It involves a process:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Collect Data:</strong> Gathering information from a sample.</li>
              <li><strong>Describe Data (Exploratory Data Analysis):</strong> Organizing and summarizing data using graphs and numerical summaries (Unit 1 focuses here).</li>
              <li><strong>Analyze Data (Inference):</strong> Using the sample data to draw conclusions about the larger population.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Variation</h3>
            <KeyTerm 
              term="Variation" 
              definition="How data values differ from one another. Data varies—if you measure the height of every student in a class, the numbers will not be identical."
            />
            <p className="text-slate-700 leading-relaxed mb-4">
              Statistics provides the tools to understand <em>why</em> data varies and if that variation is meaningful (pattern) or just random noise (chance).
            </p>
            <ImportantNote>
              <strong>Context is King:</strong> Numbers without context are meaningless. In AP Statistics, every answer must be rooted in the specific context of the problem (e.g., "The mean height of <em>11th-grade boys</em>," not just "The mean is 70").
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: The Language of Variation: Variables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Individuals vs. Variables</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Individuals (Cases/Observational Units)" 
                definition="The objects described by a set of data. They can be people (students), animals (penguins), or things (batteries)."
              />
              <KeyTerm 
                term="Variable" 
                definition="Any characteristic of an individual. A variable can take different values for different individuals."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Variables</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              It is crucial to classify variables correctly to choose the right graph and inference procedure.
            </p>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-900 mb-2">A. Categorical (Qualitative) Variables</h4>
              <p className="text-sm text-slate-700 mb-2">Places an individual into one of several groups or categories.</p>
              <p className="text-sm text-slate-600"><em>Examples:</em> Eye color, Zip Code (even though it is a number, it is a label), Grade Level, Yes/No response.</p>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900 mb-2">B. Quantitative (Numerical) Variables</h4>
              <p className="text-sm text-slate-700 mb-2">Takes numerical values for which it makes sense to find an average.</p>
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Discrete Quantitative:</strong> Can only take specific, countable values (usually integers). "Gaps" exist between values. <em>Example:</em> Number of siblings, shoe size.</li>
                <li><strong>Continuous Quantitative:</strong> Can take any value within an interval. Limited only by the precision of the measuring instrument. <em>Example:</em> Height, weight, time to run a mile.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Representing a Categorical Variable with Tables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Frequency Tables</h3>
            <KeyTerm 
              term="Frequency Table" 
              definition="Displays the count (number) of observations in each category."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Relative Frequency Tables</h3>
            <KeyTerm 
              term="Relative Frequency Table" 
              definition="Displays the proportion or percentage of observations in each category."
            />
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 my-4">
              <h4 className="font-semibold text-slate-800 mb-2">Formula</h4>
              <BlockMath math="\text{Relative Frequency} = \frac{\text{Count in Category}}{\text{Total Count}}" />
            </div>
            <ImportantNote>
              <strong>AP Tip:</strong> When comparing groups of different sizes, always use <strong>relative frequencies</strong> (percentages) rather than counts.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Representing a Categorical Variable with Graphs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Bar Charts (Bar Graphs)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Usage:</strong> Shows the distribution of a categorical variable.</li>
              <li><strong>Axes:</strong> X-axis shows categories; Y-axis shows frequency (count) or relative frequency (percent).</li>
              <li><strong>Structure:</strong> Bars should have equal widths and <strong>spaces between them</strong> (unlike histograms).</li>
              <li><strong>The Area Principle:</strong> The area of the bar should be proportional to the frequency. Avoid "pictographs" where images are scaled up, as they distort the area and mislead the viewer.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Pie Charts</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Usage:</strong> Shows the distribution of a categorical variable as slices of a whole.</li>
              <li><strong>Constraint:</strong> Categories must represent parts of one whole (must sum to 100%).</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Representing a Quantitative Variable with Graphs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Dotplots</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Usage:</strong> Small to medium quantitative data sets.</li>
              <li><strong>Construction:</strong> Draw a number line. Place a dot above the value for each observation.</li>
              <li><strong>Pros:</strong> Preserves exact data values; easy to see shape and clusters.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Stemplots (Stem-and-Leaf Plots)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Usage:</strong> Small quantitative data sets.</li>
              <li><strong>Construction:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li><strong>Stem:</strong> All digits except the last one.</li>
                  <li><strong>Leaf:</strong> The final digit.</li>
                  <li><strong>Key:</strong> Crucial for interpretation (e.g., Key: <InlineMath math="4|5" /> represents 45 minutes).</li>
                </ul>
              </li>
              <li><strong>Splitting Stems:</strong> If data is clumped, you can split stems (e.g., 1L for 10-14, 1H for 15-19) to see the shape better.</li>
              <li><strong>Pros:</strong> Preserves exact values; shows shape.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Histograms</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Usage:</strong> Large quantitative data sets.</li>
              <li><strong>Construction:</strong> Divide data into classes (bins) of equal width. The height of the bar corresponds to the frequency (or relative frequency) of data points in that interval.</li>
              <li><strong>Rule:</strong> Bars <strong>touch</strong> (no gaps) unless there is an empty class.</li>
              <li><strong>Warning:</strong> Histograms do <em>not</em> display individual data values.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Describing the Distribution of a Quantitative Variable",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When asked to describe a distribution, you must address four components. Use the acronym <strong>SOCS</strong>.
          </p>
          <Mnemonic title="SOCS" content="Shape, Outliers, Center, Spread — the four things you must address when describing a distribution." />

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Shape (S)</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Symmetric:</strong> Left and right sides are mirror images.</li>
              <li><strong>Skewed Right (Positively Skewed):</strong> The tail extends to the right (towards higher numbers).</li>
              <li><strong>Skewed Left (Negatively Skewed):</strong> The tail extends to the left (towards lower numbers).</li>
              <li><strong>Modes:</strong> Unimodal (one peak), Bimodal (two peaks), Uniform (flat/no peak).</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Outliers (O)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Mention any unusual points that fall far away from the bulk of the data. In Topic 1.6, visual estimation is acceptable. In Topics 1.7/1.8, we calculate them mathematically.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Center (C)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">A typical value.</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>If the shape is <strong>Symmetric</strong>: Use the <strong>Mean</strong>.</li>
              <li>If the shape is <strong>Skewed</strong> or has <strong>Outliers</strong>: Use the <strong>Median</strong>.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Spread (S) / Variability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">How far apart the data values are.</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>If the shape is <strong>Symmetric</strong>: Use <strong>Standard Deviation</strong>.</li>
              <li>If the shape is <strong>Skewed</strong> or has <strong>Outliers</strong>: Use <strong>IQR (Interquartile Range)</strong> or Range.</li>
            </ul>
          </section>

          <ImportantNote>
            <strong>AP Exam Tip:</strong> Always describe distributions <strong>in context</strong>. Do not just list "Skewed right, median 5." Write: "The distribution of <em>number of pets</em> is skewed right with a median of 5."
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Summary Statistics for a Quantitative Variable",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Measures of Center</h3>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">Mean (<InlineMath math="\bar{x}" />)</h4>
              <p className="text-sm text-slate-700 mt-1">The arithmetic average.</p>
              <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                <BlockMath math="\bar{x} = \frac{\sum x_i}{n}" />
              </div>
              <p className="text-sm text-red-700 mt-2"><strong>Non-resistant:</strong> Strongly affected by skewness and outliers. The mean is pulled toward the tail.</p>
            </div>

            <KeyTerm 
              term="Median" 
              definition="The midpoint of a distribution. Resistant: Not affected by outliers or skewness."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Measures of Spread</h3>
            
            <div className="space-y-4">
              <KeyTerm term="Range" definition="Max - Min. (Very non-resistant)." />
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Interquartile Range (IQR)</h4>
                <div className="mt-2">
                  <BlockMath math="IQR = Q3 - Q1" />
                </div>
                <p className="text-sm text-slate-700 mt-2">Represents the middle 50% of the data. <strong>Resistant</strong> measure of spread.</p>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">Standard Deviation (<InlineMath math="s_x" />)</h4>
                <p className="text-sm text-slate-700 mt-1">The average distance of data points from the mean.</p>
                <div className="mt-3 bg-white p-3 rounded border border-amber-200">
                  <BlockMath math="s_x = \sqrt{\frac{\sum (x_i - \bar{x})^2}{n-1}}" />
                </div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                  <li><strong>Non-resistant:</strong> If the mean moves, the standard deviation is affected.</li>
                  <li><InlineMath math="s_x" /> is always <InlineMath math="\geq 0" />. It is 0 only if all data values are equal.</li>
                </ul>
              </div>

              <KeyTerm term="Variance" definition="The standard deviation squared (s²)." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-8",
      title: "Topic 1.8: Graphical Representations of Summary Statistics (Boxplots)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Five-Number Summary</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700 mb-4">
              <li>Minimum</li>
              <li>Q1 (First Quartile - 25th percentile)</li>
              <li>Median (50th percentile)</li>
              <li>Q3 (Third Quartile - 75th percentile)</li>
              <li>Maximum</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Identifying Outliers (The 1.5 IQR Rule)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">A data point is an outlier if:</p>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <BlockMath math="\text{Value} < Q1 - 1.5 \times IQR" />
              <p className="text-center text-slate-600 my-2">OR</p>
              <BlockMath math="\text{Value} > Q3 + 1.5 \times IQR" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Boxplots</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Box:</strong> Spans from Q1 to Q3 (the IQR). Line inside the box marks the median.</li>
              <li><strong>Whiskers:</strong> Extend to the lowest and highest observations <em>that are not outliers</em>.</li>
              <li><strong>Outliers:</strong> Marked as distinct dots or asterisks beyond the whiskers.</li>
            </ul>
            <ImportantNote>
              <strong>AP Tip:</strong> Boxplots show symmetry and skewness well, but they hide clusters, gaps, and the number of peaks (modes). You cannot tell if a distribution is bimodal from a boxplot.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-9",
      title: "Topic 1.9: Comparing Distributions of a Quantitative Variable",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When asked to compare two or more distributions (e.g., parallel boxplots or side-by-side histograms), you must:
          </p>
          <ol className="list-decimal pl-6 space-y-3 text-slate-700 mb-4">
            <li>Address <strong>SOCS</strong> (Shape, Outliers, Center, Spread).</li>
            <li>
              Use <strong>Comparative Language</strong>.
              <div className="mt-2 ml-4 p-3 bg-red-50 rounded border border-red-200">
                <p className="text-sm"><strong>Bad:</strong> "Group A has a median of 5. Group B has a median of 10."</p>
              </div>
              <div className="mt-2 ml-4 p-3 bg-green-50 rounded border border-green-200">
                <p className="text-sm"><strong>Good:</strong> "The median of Group B (10) is <strong>greater than</strong> the median of Group A (5)."</p>
              </div>
            </li>
            <li>Use <strong>Context</strong>.</li>
          </ol>
        </div>
      )
    },
    {
      id: "topic-1-10",
      title: "Topic 1.10: The Normal Distribution",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Normal Density Curve</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>Symmetric, single-peaked (unimodal), and bell-shaped.</li>
              <li>Defined by mean (<InlineMath math="\mu" />) and standard deviation (<InlineMath math="\sigma" />).</li>
              <li>The mean, median, and mode are all at the center.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Empirical Rule (68-95-99.7 Rule)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">For a Normal distribution:</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                <li>~<strong>68%</strong> of data falls within <InlineMath math="\mu \pm 1\sigma" /></li>
                <li>~<strong>95%</strong> of data falls within <InlineMath math="\mu \pm 2\sigma" /></li>
                <li>~<strong>99.7%</strong> of data falls within <InlineMath math="\mu \pm 3\sigma" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Standard Normal Distribution</h3>
            <KeyTerm term="Standard Normal Distribution" definition="A Normal distribution with Mean = 0 and Standard Deviation = 1." />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Z-Scores (Standardized Scores)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Tells us how many standard deviations a value (<InlineMath math="x" />) is above or below the mean.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="z = \frac{x - \mu}{\sigma}" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Interpretation:</strong> "The value <InlineMath math="x" /> is [z-score] standard deviations [above/below] the mean."</li>
              <li>Standardizing data shifts the mean to 0 and scales the standard deviation to 1, but it <strong>does not change the shape</strong> of the distribution.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding Area/Proportion</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Table A (Z-table):</strong> Gives the area to the <em>left</em> of a z-score.</li>
              <li><strong>Calculator:</strong> <code className="bg-slate-200 px-1 rounded">normalcdf(lower, upper, mean, SD)</code></li>
              <li><strong>Finding Value from Area:</strong> <code className="bg-slate-200 px-1 rounded">invNorm(area to the left, mean, SD)</code></li>
            </ul>
            <ImportantNote>
              <strong>AP Tip:</strong> When using a calculator function like <code className="bg-slate-200 px-1 rounded">normalcdf</code> on the free-response section, do not just write "normalcdf(...)." You must label your inputs (e.g., "Lower bound: -999, Upper: 1.5, Mean: 0, SD: 1") and typically draw a sketch of the curve with the shaded region.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default statisticsUnit1Notes;
